import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import type { ReactNode } from 'react';
import { messages as enMessages } from '@/locales/en/messages.mjs';
import { messages as esMessages } from '@/locales/es/messages.mjs';

export const DEFAULT_LOCALE = 'en';
export const SUPPORTED_LOCALES = ['en', 'es'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

type I18nProviderProps = {
  children: ReactNode;
  locale?: SupportedLocale;
};

type I18nContextValue = {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
};

const STORAGE_KEY = 'jema-manager-locale';
const I18nContext = createContext<I18nContextValue | undefined>(undefined);
const hasLoaded = new Set<SupportedLocale>();

const getBrowserLocale = (): SupportedLocale | null => {
  if (typeof navigator === 'undefined') return null;
  const lang = navigator.language?.split('-')[0]?.toLowerCase();
  if (SUPPORTED_LOCALES.includes(lang as SupportedLocale)) {
    return lang as SupportedLocale;
  }
  return null;
};

const resolveInitialLocale = (): SupportedLocale => {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored as SupportedLocale)) {
      return stored as SupportedLocale;
    }
  }
  return getBrowserLocale() ?? DEFAULT_LOCALE;
};

const loadLocale = (locale: SupportedLocale) => {
  if (!hasLoaded.has('en')) {
    i18n.load('en', enMessages);
    hasLoaded.add('en');
  }
  if (!hasLoaded.has('es')) {
    i18n.load('es', esMessages);
    hasLoaded.add('es');
  }
  if (!hasLoaded.has(locale)) {
    // All locale messages are pre-bundled; nothing dynamic to fetch.
    hasLoaded.add(locale);
  }
  i18n.activate(locale);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
};

export const AppI18nProvider = ({ children, locale: initialLocale }: I18nProviderProps) => {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => {
    const resolved = initialLocale ?? resolveInitialLocale();
    // Ensure locale is loaded before first render
    if (!hasLoaded.has(resolved)) {
      loadLocale(resolved);
    }
    return resolved;
  });

  useEffect(() => {
    loadLocale(locale);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, locale);
    }
  }, [locale]);

  const setLocale = useCallback((next: SupportedLocale) => {
    // Prevent setting the same locale
    if (next === locale) {
      return;
    }
    setLocaleState(next);
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
    }),
    [locale, setLocale]
  );

  return (
    <I18nContext.Provider value={value}>
      <I18nProvider i18n={i18n}>{children}</I18nProvider>
    </I18nContext.Provider>
  );
};

export const useLocale = (): I18nContextValue => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useLocale must be used within AppI18nProvider');
  }
  return ctx;
};
